import { useEffect, useMemo, useState } from 'react'
import { Keyboard, KeyboardAvoidingView, Platform, Text, View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { Calendar, Check, ChevronRight, Clock, MapPin, Navigation } from '@/components/ui/icons'
import { Button } from '@/components/ui/Button'
import { DatePicker } from '@/components/ui/DatePicker'
import { LocationSearchModal, type SearchLocation } from '@/components/location/LocationSearchModal'
import { Pressable } from '@/components/ui/Pressable'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { TextInput } from '@/components/ui/TextInput'
import { useCreateServiceRequest } from '@/lib/hooks/use-services'
import { useTheme } from '@/lib/hooks/use-theme'
import {
  SERVICE_CATALOG,
  isServiceCategory,
  serviceCategoryById,
  serviceNeedRequiresDestination,
  type ServiceCategoryId,
  type ServiceTiming,
  type ServiceWindow,
} from '@/lib/services/services-catalog'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'
import { selection } from '@/lib/utils/haptics'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { INTERACTION, useSemanticColors } from '@/lib/theme/tokens'
import { ServiceCategoryIcon } from './ServiceCategoryIcon'

function localIsoDate(offsetDays = 0): string {
  const value = new Date()
  value.setHours(12, 0, 0, 0)
  value.setDate(value.getDate() + offsetDays)
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formErrorKey(code: string | undefined): string {
  switch (code) {
    case 'SERVICES_NATIONAL_ONLY':
      return 'services.form.error_national'
    case 'SERVICES_LOCATION_UNAVAILABLE':
      return 'services.form.error_location'
    case 'SERVICES_RATE_LIMITED':
      return 'services.form.error_rate_limited'
    case 'SERVICES_VALIDATION_ERROR':
      return 'services.form.error_incomplete'
    default:
      return 'services.form.error_submit'
  }
}

function ChoiceChip({
  label,
  selected,
  onPress,
  testID,
}: {
  label: string
  selected: boolean
  onPress: () => void
  testID: string
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      accessibilityLabel={label}
      testID={testID}
      style={({ pressed }) => ({
        minHeight: INTERACTION.minTouchTarget,
        borderRadius: RADIUS.full,
        borderWidth: 1,
        borderColor: selected ? COLORS.coral.dark : colors.border,
        backgroundColor: selected
          ? COLORS.coral.dark
          : isDark
            ? colors.surfaceRaised
            : colors.surface,
        paddingHorizontal: SPACING.md,
        justifyContent: 'center',
        opacity: pressed ? 0.82 : 1,
      })}
    >
      <Text
        numberOfLines={1}
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 13,
          color: selected ? COLORS.white : colors.textPrimary,
        }}
      >
        {label}
      </Text>
    </Pressable>
  )
}

function SectionLabel({ children }: { children: string }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 17, color: colors.textPrimary }}>
      {children}
    </Text>
  )
}

/** One field shell for both ends of a tow without duplicating focus semantics. */
function LocationField({
  label,
  hint,
  placeholder,
  location,
  onPress,
  testID,
  destination = false,
}: {
  label: string
  hint: string
  placeholder: string
  location: SearchLocation | null
  onPress: () => void
  testID: string
  destination?: boolean
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const Icon = destination ? Navigation : MapPin
  const accent = destination ? COLORS.purple.DEFAULT : COLORS.coral.DEFAULT

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={location ? `${label}: ${location.label}` : label}
      testID={testID}
      style={({ pressed }) => ({
        minHeight: 66,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.surfaceSubtle,
        paddingHorizontal: SPACING.md,
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
        opacity: pressed ? 0.82 : 1,
      })}
    >
      <Icon size={21} color={accent} strokeWidth={2.1} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textSecondary }}>
          {hint}
        </Text>
        <Text
          numberOfLines={2}
          style={{
            marginTop: 2,
            fontFamily: FONTS.bodyBold,
            fontSize: 14,
            color: location ? colors.textPrimary : colors.textMuted,
          }}
        >
          {location?.label ?? placeholder}
        </Text>
      </View>
      <ChevronRight size={18} color={colors.textSecondary} strokeWidth={2} />
    </Pressable>
  )
}

export function ServiceRequestForm() {
  const { t } = useTranslation()
  const router = useRouter()
  const params = useLocalSearchParams<{ category?: string }>()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const createRequest = useCreateServiceRequest()
  const initialCategory = isServiceCategory(params.category) ? params.category : 'tow'
  const [categoryId, setCategoryId] = useState<ServiceCategoryId>(initialCategory)
  const category = useMemo(() => serviceCategoryById(categoryId), [categoryId])
  const [need, setNeed] = useState(category.needs[0]!.id)
  const [location, setLocation] = useState<SearchLocation | null>(null)
  const [destination, setDestination] = useState<SearchLocation | null>(null)
  const [locationPickerTarget, setLocationPickerTarget] = useState<
    'location' | 'destination' | null
  >(null)
  const [timing, setTiming] = useState<ServiceTiming>(category.defaultTiming)
  const [scheduledDate, setScheduledDate] = useState<string | null>(
    category.defaultTiming === 'scheduled' ? localIsoDate(1) : null,
  )
  const [arrivalWindow, setArrivalWindow] = useState<ServiceWindow>('flexible')
  const [details, setDetails] = useState('')
  const [errorKey, setErrorKey] = useState<string | null>(null)

  useEffect(() => {
    if (isServiceCategory(params.category) && params.category !== categoryId) {
      setCategoryId(params.category)
    }
  }, [categoryId, params.category])

  useEffect(() => {
    setNeed(category.needs[0]!.id)
    setTiming(category.defaultTiming)
    setScheduledDate(category.defaultTiming === 'scheduled' ? localIsoDate(1) : null)
  }, [category])

  const requiresDestination = serviceNeedRequiresDestination(category.id, need)

  useEffect(() => {
    if (!requiresDestination) setDestination(null)
  }, [requiresDestination])

  const selectCategory = (next: ServiceCategoryId) => {
    if (next === categoryId) return
    setCategoryId(next)
    setErrorKey(null)
    void selection()
  }

  const chooseTiming = (next: ServiceTiming) => {
    setTiming(next)
    setScheduledDate(next === 'scheduled' ? (scheduledDate ?? localIsoDate(1)) : null)
    void selection()
  }

  const ready = Boolean(
    location &&
    (!requiresDestination || destination) &&
    details.trim().length >= 12 &&
    (timing === 'now' || scheduledDate),
  )

  const submit = async () => {
    if (!location || (requiresDestination && !destination) || !ready) {
      setErrorKey('services.form.error_incomplete')
      return
    }
    setErrorKey(null)
    const idempotencyKey = `service-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    try {
      const request = await createRequest.mutateAsync({
        idempotencyKey,
        category: category.id,
        need,
        location,
        destination: requiresDestination ? destination : null,
        timing,
        scheduledDate,
        arrivalWindow,
        details: details.trim(),
      })
      productTelemetryService.recordEvent('form_submit', 'services.request_submitted', {
        elementId: 'services.request-submit',
        elementType: 'button',
        metadata: { category: category.id, timing },
      })
      // The multiline details field can still own Android's IME after a route
      // replacement. Dismiss it explicitly so the confirmation starts clean.
      Keyboard.dismiss()
      router.replace(`/services/${request.id}` as never)
    } catch (error) {
      setErrorKey(formErrorKey((error as { code?: string }).code))
    }
  }

  return (
    <ScreenContainer variant="content" scroll={false}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={
          Platform.OS === 'ios' ? 'padding' : Platform.OS === 'android' ? 'height' : undefined
        }
      >
        <ScreenHeader
          title={t('services.form.title')}
          subtitle={t('services.form.subtitle')}
          showBack
          onBackPress={() => router.back()}
          style={{ paddingHorizontal: SPACING.lg, paddingTop: SPACING.md }}
        />
        <ScrollView
          style={{ flex: 1 }}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ padding: SPACING.lg, paddingTop: SPACING.md, gap: SPACING.xl }}
          showsVerticalScrollIndicator={false}
        >
          <View style={{ gap: SPACING.sm }}>
            <SectionLabel>{t('services.form.service_label')}</SectionLabel>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.xs }}>
              {SERVICE_CATALOG.map((candidate) => (
                <ChoiceChip
                  key={candidate.id}
                  label={t(candidate.titleKey)}
                  selected={candidate.id === category.id}
                  onPress={() => selectCategory(candidate.id)}
                  testID={`services.form.category.${candidate.id}`}
                />
              ))}
            </View>
          </View>

          <View
            style={{
              borderRadius: RADIUS.xl,
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(91,84,144,0.14)',
              backgroundColor: isDark ? 'rgba(91,84,144,0.14)' : 'rgba(91,84,144,0.05)',
              padding: SPACING.md,
              flexDirection: 'row',
              gap: SPACING.sm,
              alignItems: 'center',
            }}
          >
            <ServiceCategoryIcon category={category} size={20} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 15, color: colors.textPrimary }}>
                {t(category.titleKey)}
              </Text>
              <Text
                style={{
                  marginTop: 2,
                  fontFamily: FONTS.body,
                  fontSize: 12,
                  lineHeight: 17,
                  color: colors.textSecondary,
                }}
              >
                {t(category.descriptionKey)}
              </Text>
            </View>
          </View>

          <View style={{ gap: SPACING.sm }}>
            <SectionLabel>{t('services.form.need_label')}</SectionLabel>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.xs }}>
              {category.needs.map((candidate) => (
                <ChoiceChip
                  key={candidate.id}
                  label={t(candidate.titleKey)}
                  selected={candidate.id === need}
                  onPress={() => {
                    setNeed(candidate.id)
                    void selection()
                  }}
                  testID={`services.form.need.${candidate.id}`}
                />
              ))}
            </View>
          </View>

          <View style={{ gap: SPACING.sm }}>
            <SectionLabel>{t('services.form.location_label')}</SectionLabel>
            <LocationField
              label={t('services.form.location_label')}
              hint={t('services.form.location_hint')}
              placeholder={t('services.form.location_placeholder')}
              location={location}
              onPress={() => setLocationPickerTarget('location')}
              testID="services.form.location"
            />
          </View>

          {requiresDestination ? (
            <View style={{ gap: SPACING.sm }}>
              <SectionLabel>{t('services.form.destination_label')}</SectionLabel>
              <LocationField
                label={t('services.form.destination_label')}
                hint={t('services.form.destination_hint')}
                placeholder={t('services.form.destination_placeholder')}
                location={destination}
                onPress={() => setLocationPickerTarget('destination')}
                testID="services.form.destination"
                destination
              />
            </View>
          ) : null}

          <View style={{ gap: SPACING.sm }}>
            <SectionLabel>{t('services.form.when_label')}</SectionLabel>
            <View style={{ flexDirection: 'row', gap: SPACING.xs }}>
              <ChoiceChip
                label={t('services.form.timing_now')}
                selected={timing === 'now'}
                onPress={() => chooseTiming('now')}
                testID="services.form.timing.now"
              />
              <ChoiceChip
                label={t('services.form.timing_scheduled')}
                selected={timing === 'scheduled'}
                onPress={() => chooseTiming('scheduled')}
                testID="services.form.timing.scheduled"
              />
            </View>
            {timing === 'scheduled' ? (
              <View style={{ gap: SPACING.sm }}>
                <DatePicker
                  value={scheduledDate}
                  onChange={setScheduledDate}
                  minDate={localIsoDate()}
                  label={t('services.form.date_label')}
                  testID="services.form.scheduled-date"
                />
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.xs }}>
                  {(['morning', 'afternoon', 'flexible'] as const).map((window) => (
                    <ChoiceChip
                      key={window}
                      label={t(`services.form.window_${window}`)}
                      selected={arrivalWindow === window}
                      onPress={() => {
                        setArrivalWindow(window)
                        void selection()
                      }}
                      testID={`services.form.window.${window}`}
                    />
                  ))}
                </View>
              </View>
            ) : (
              <View
                style={{
                  borderRadius: RADIUS.md,
                  backgroundColor: isDark ? 'rgba(242,180,65,0.12)' : 'rgba(242,180,65,0.15)',
                  padding: SPACING.sm,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: SPACING.xs,
                }}
              >
                <Clock size={17} color="#9A6511" strokeWidth={2} />
                <Text
                  style={{
                    flex: 1,
                    fontFamily: FONTS.body,
                    fontSize: 12,
                    lineHeight: 17,
                    color: colors.textSecondary,
                  }}
                >
                  {t('services.form.now_note')}
                </Text>
              </View>
            )}
          </View>

          <TextInput
            value={details}
            onChangeText={(value) => {
              setDetails(value)
              if (errorKey) setErrorKey(null)
            }}
            label={t('services.form.details_label')}
            placeholder={t('services.form.details_placeholder')}
            multiline
            numberOfLines={4}
            textAlignVertical="top"
            maxLength={1200}
            accessibilityLabel={t('services.form.details_label')}
            testID="services.form.details"
            style={{ minHeight: 118, paddingTop: SPACING.md }}
          />

          <View
            style={{
              borderRadius: RADIUS.lg,
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(26,21,37,0.08)',
              backgroundColor: colors.surface,
              padding: SPACING.md,
              flexDirection: 'row',
              gap: SPACING.sm,
            }}
          >
            <Calendar size={19} color={COLORS.purple.DEFAULT} strokeWidth={2} />
            <Text
              style={{
                flex: 1,
                fontFamily: FONTS.body,
                fontSize: 12,
                lineHeight: 18,
                color: colors.textSecondary,
              }}
            >
              {t('services.form.contact_note')}
            </Text>
          </View>
        </ScrollView>

        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: colors.border,
            backgroundColor: colors.background,
            paddingHorizontal: SPACING.lg,
            paddingTop: SPACING.md,
            paddingBottom: SPACING.lg,
            gap: SPACING.xs,
          }}
        >
          {errorKey ? (
            <Text
              accessibilityRole="alert"
              style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textError }}
            >
              {t(errorKey)}
            </Text>
          ) : null}
          <Button
            title={t('services.form.submit')}
            size="lg"
            loading={createRequest.isPending}
            disabled={!ready}
            onPress={submit}
            icon={<Check size={20} />}
            testID="services.form.submit"
          />
        </View>
      </KeyboardAvoidingView>

      <LocationSearchModal
        visible={locationPickerTarget !== null}
        title={t(
          locationPickerTarget === 'destination'
            ? 'services.form.destination_picker_title'
            : 'services.form.location_picker_title',
        )}
        onClose={() => setLocationPickerTarget(null)}
        onPick={(next) => {
          if (locationPickerTarget === 'destination') {
            setDestination(next)
          } else {
            setLocation(next)
          }
          setLocationPickerTarget(null)
          setErrorKey(null)
          void selection()
        }}
        testIDPrefix={
          locationPickerTarget === 'destination'
            ? 'services.form.destination-search'
            : 'services.form.location-search'
        }
        placeholder={t('services.form.location_search_placeholder')}
        searchingLabel={t('services.form.location_searching')}
        emptyLabel={t('services.form.location_empty')}
        errorLabel={t('services.form.location_search_error')}
      />
    </ScreenContainer>
  )
}
